import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, AlertTriangle, Wrench, FileX, CalendarRange, FileText } from "lucide-react";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

async function confirmTwice(msg) {
  if (!(await confirmDialog(msg))) return false;
  if (!(await confirmDialog(`ΕΠΙΒΕΒΑΙΩΣΗ: ${msg}\n\nΕίστε σίγουρος;`))) return false;
  return true;
}

const TYPE_OPTIONS = [
  { value: "any", label: "Όλοι οι τύποι" },
  { value: "receipt", label: "Απόδειξη Λιανικής" },
  { value: "credit_receipt", label: "Τιμολόγιο" },
  { value: "restaurant_order", label: "Παραγγελία Τραπεζιού" },
  { value: "restaurant_close", label: "Κλείσιμο Τραπεζιού" },
  { value: "non_fiscal_order", label: "Μη Φορολογική Παραγγελία" },
  { value: "zreport", label: "Αναφορά Z" },
  { value: "xreport", label: "Αναφορά X" },
  { value: "status", label: "Status" },
  { value: "raw", label: "Raw" },
];

const PRESET_OPTIONS = [
  { value: "today", label: "Σήμερα" },
  { value: "yesterday", label: "Χθες" },
  { value: "this_week", label: "Τρέχουσα Εβδομάδα" },
  { value: "last_week", label: "Προηγούμενη Εβδομάδα" },
  { value: "this_month", label: "Τρέχων Μήνας" },
  { value: "last_month", label: "Προηγούμενος Μήνας" },
];

function presetRange(key) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  const monday = (d) => {
    const dt = new Date(d);
    const day = (dt.getDay() + 6) % 7; // 0=Mon...6=Sun
    dt.setDate(dt.getDate() - day);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };

  switch (key) {
    case "today":
      return [start, end];
    case "yesterday": {
      const s = new Date(start);
      s.setDate(s.getDate() - 1);
      const e = new Date(end);
      e.setDate(e.getDate() - 1);
      return [s, e];
    }
    case "this_week": {
      const s = monday(now);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case "last_week": {
      const s = monday(now);
      s.setDate(s.getDate() - 7);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case "this_month": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return [s, e];
    }
    case "last_month": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return [s, e];
    }
    default:
      return [null, null];
  }
}

export default function TableManagementPanel() {
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState("receipts");
  const [saleClass, setSaleClass] = useState("any");
  const [type, setType] = useState("any");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [quickPreset, setQuickPreset] = useState("today");

  const load = useCallback(async () => {
    try {
      const r = await http.get("/tables/list");
      setTables(r.data.items || []);
    } catch {
      toast.error("Σφάλμα φόρτωσης");
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, [load]);

  const requestCode = async () => (await promptDialog("Κωδικός διαχειριστή για επιβεβαίωση:"));

  const askDeleteRange = async (target, body, label) => {
    if (!(await confirmTwice(`${label}\n\nΘα διαγραφούν εγγραφές από «${target}» με τα επιλεγμένα φίλτρα.`))) return;
    const code = await requestCode();
    if (!code) return;
    try {
      const r = await http.post(`/tables/${target}/delete-range`, { ...body, admin_code: code });
      toast.success(`Διαγράφηκαν ${r.data.deleted} εγγραφές`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία διαγραφής");
    }
  };

  // ---------- Quick actions ----------
  const onClearAllMovements = async () => {
    await askDeleteRange("receipts", {}, "Εκκαθάριση ΟΛΩΝ των κινήσεων (Ιστορικό Αποδείξεων)");
  };

  const onClearMovementsRange = async () => {
    const [s, e] = presetRange(quickPreset);
    if (!s || !e) {
      toast.error("Επιλέξτε έγκυρο εύρος");
      return;
    }
    const label = PRESET_OPTIONS.find((o) => o.value === quickPreset)?.label || quickPreset;
    await askDeleteRange(
      "receipts",
      { date_from: s.toISOString(), date_to: e.toISOString() },
      `Εκκαθάριση κινήσεων: ${label}\n(${s.toLocaleDateString("el-GR")} → ${e.toLocaleDateString("el-GR")})`
    );
  };

  const onClearLogs = async () => {
    await askDeleteRange("app_logs", {}, "Εκκαθάριση ΟΛΩΝ των logs εφαρμογής");
  };

  // ---------- Manual filters ----------
  const onDeleteRange = async () => {
    const isReceipts = selected === "receipts";
    const summary = [
      `Πίνακας: ${selected}`,
      dateFrom ? `Από: ${dateFrom}` : null,
      dateTo ? `Έως: ${dateTo}` : null,
      isReceipts && saleClass !== "any" ? `Κατηγορία: ${saleClass}` : null,
      isReceipts && type !== "any" ? `Τύπος: ${type}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    const noFilters = !dateFrom && !dateTo && saleClass === "any" && type === "any";
    const baseMsg = noFilters
      ? `⚠️ Δεν έχετε ορίσει φίλτρα.\nΘα διαγραφούν ΟΛΕΣ οι εγγραφές του πίνακα «${selected}».`
      : `Διαγραφή εγγραφών:\n${summary}`;
    if (!(await confirmTwice(baseMsg))) return;
    const code = requestCode();
    if (!code) return;
    try {
      const body = { admin_code: code };
      if (dateFrom) body.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        const d = new Date(dateTo);
        d.setHours(23, 59, 59, 999);
        body.date_to = d.toISOString();
      }
      if (isReceipts && saleClass !== "any") body.sale_class = saleClass;
      if (isReceipts && type !== "any") body.type = type;
      const r = await http.post(`/tables/${selected}/delete-range`, body);
      toast.success(`Διαγράφηκαν ${r.data.deleted} εγγραφές`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία διαγραφής");
    }
  };

  const onTruncate = async () => {
    if (!(await confirmTwice(`⚠️ ΟΛΟΚΛΗΡΩΤΙΚΗ ΔΙΑΓΡΑΦΗ του πίνακα «${selected}»;`))) return;
    const code = requestCode();
    if (!code) return;
    try {
      const r = await http.post(`/tables/${selected}/truncate`, { admin_code: code });
      toast.success(`Διαγράφηκαν ${r.data.deleted} εγγραφές`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  return (
    <Card className="p-6 space-y-5" data-testid="table-mgmt-panel">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
        <Wrench size={18} /> Διαχείριση Πινάκων
      </h3>

      {/* Quick actions */}
      <div className="space-y-3">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-600">
          Γρήγορες Ενέργειες
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* 1. Clear all movements */}
          <button
            type="button"
            onClick={onClearAllMovements}
            className="text-left p-4 border-2 border-red-200 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
            data-testid="quick-clear-movements"
          >
            <div className="flex items-center gap-2 mb-1">
              <FileX size={18} className="text-red-700" />
              <span className="font-bold text-sm text-red-900">Εκκαθάριση Κινήσεων</span>
            </div>
            <div className="text-[11px] text-red-700/80">
              Διαγραφή ΟΛΟΥ του ιστορικού αποδείξεων (AA + BB).
            </div>
          </button>

          {/* 2. Clear movements with preset range */}
          <div className="p-4 border-2 border-amber-200 bg-amber-50 rounded-lg" data-testid="quick-clear-range">
            <div className="flex items-center gap-2 mb-1">
              <CalendarRange size={18} className="text-amber-700" />
              <span className="font-bold text-sm text-amber-900">Εκκαθάριση Κινήσεων (Εύρος)</span>
            </div>
            <div className="text-[11px] text-amber-700/80 mb-2">
              Διαγραφή για προκαθορισμένη περίοδο.
            </div>
            <div className="flex gap-2 items-end">
              <Select value={quickPreset} onValueChange={setQuickPreset}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="quick-range-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRESET_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={onClearMovementsRange}
                size="sm"
                className="h-8 bg-amber-600 hover:bg-amber-700"
                data-testid="quick-clear-range-btn"
              >
                Εκκαθάριση
              </Button>
            </div>
          </div>

          {/* 3. Clear logs */}
          <button
            type="button"
            onClick={onClearLogs}
            className="text-left p-4 border-2 border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="quick-clear-logs"
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText size={18} className="text-slate-700" />
              <span className="font-bold text-sm text-slate-900">Εκκαθάριση Logs</span>
            </div>
            <div className="text-[11px] text-slate-600">
              Διαγραφή ΟΛΩΝ των logs εφαρμογής.
            </div>
          </button>
        </div>
      </div>

      {/* Manual filters */}
      <div className="pt-3 border-t border-slate-200 space-y-3">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-600">
          Προχωρημένη Διαγραφή
        </div>
        <div className="text-[12px] text-slate-500">
          Διαγραφή κινήσεων με προσαρμοσμένα φίλτρα. Διπλή επιβεβαίωση + κωδικός διαχειριστή.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Πίνακας</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="h-9" data-testid="tmgmt-table">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tables.map((t) => (
                  <SelectItem key={t.name} value={t.name}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Από</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="tmgmt-from" />
          </div>
          <div>
            <Label className="text-xs">Έως</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="tmgmt-to" />
          </div>
          {selected === "receipts" && (
            <>
              <div>
                <Label className="text-xs">Κατηγορία (AA/BB)</Label>
                <Select value={saleClass} onValueChange={setSaleClass}>
                  <SelectTrigger className="h-9" data-testid="tmgmt-saleclass">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Όλες</SelectItem>
                    <SelectItem value="AA">AA – Ταμειακή</SelectItem>
                    <SelectItem value="BB">BB – Δισκέτα</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Τύπος</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="h-9" data-testid="tmgmt-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onDeleteRange} className="bg-red-600 hover:bg-red-700 h-9" data-testid="tmgmt-delete-range">
            <Trash2 size={14} className="mr-1.5" /> Διαγραφή με τα παραπάνω φίλτρα
          </Button>
          <Button onClick={onTruncate} variant="outline" className="h-9 text-red-700 border-red-200" data-testid="tmgmt-truncate">
            <AlertTriangle size={14} className="mr-1.5" /> Πλήρης διαγραφή πίνακα
          </Button>
        </div>
      </div>
    </Card>
  );
}
